import {
    RuleTypeDescription,
    RuleScopeProperty,
    RuleScopeLogin
} from '../consts';

import { availableLanguages } from '../common';

const escape = ( s ) => s.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' );

const RuleKeyDescriptionRemoveText = 'remove-text';
const RuleKeyDescriptionRemoveRange = 'remove-range';
const RuleKeyDescriptionRemoveIfContains = 'remove-contains';
const RuleKeyDescriptionOverride = 'remove-override';
const RuleKeyDescriptionCapitalisation = 'remove-uppercase';

const allowedKeys = [
    RuleKeyDescriptionRemoveText,
    RuleKeyDescriptionRemoveRange,
    RuleKeyDescriptionRemoveIfContains,
    RuleKeyDescriptionOverride,
    RuleKeyDescriptionCapitalisation
];

const allowedKeyScopes = {
    [ RuleKeyDescriptionRemoveText ]: [ RuleScopeProperty, RuleScopeLogin ],
    [ RuleKeyDescriptionRemoveRange ]: [ RuleScopeProperty, RuleScopeLogin ],
    [ RuleKeyDescriptionRemoveIfContains ]: [ RuleScopeProperty, RuleScopeLogin ],
    [ RuleKeyDescriptionOverride ]: [ RuleScopeProperty ],
    [ RuleKeyDescriptionCapitalisation ]: [ RuleScopeProperty ]
};

const removeTemplate = {
    key: RuleKeyDescriptionRemoveText,
    type: RuleTypeDescription,
    language: '',
    textToMatch: '',
    scope: ''
};

const rangeTemplate = {
    key: RuleKeyDescriptionRemoveRange,
    type: RuleTypeDescription,
    language: '',
    rangeStart: '',
    rangeEnd: '',
    scope: ''
};

const containsTemplate = {
    key: RuleKeyDescriptionRemoveIfContains,
    type: RuleTypeDescription,
    language: '',
    textToMatch: '',
    scope: ''
};

const overrideTemplate = {
    key: RuleKeyDescriptionOverride,
    type: RuleTypeDescription,
    language: '',
    text: '',
    scope: ''
};

const capitalisationTemplate = {
    key: RuleKeyDescriptionCapitalisation,
    type: RuleTypeDescription,
    language: '',
    scope: ''
};

const ruleTemplates = {
    [ RuleKeyDescriptionOverride ]: overrideTemplate,
    [ RuleKeyDescriptionRemoveIfContains ]: containsTemplate,
    [ RuleKeyDescriptionRemoveRange ]: rangeTemplate,
    [ RuleKeyDescriptionRemoveText ]: removeTemplate,
    [ RuleKeyDescriptionCapitalisation ]: capitalisationTemplate
};

const makeDescriptionRule = ( base, key ) => {
    let rule = null;

    if ( !allowedKeys.includes( key ) ) {
        return {
            rule: rule,
            error: new Error( `"${key}" is not a valid rule key` )
        };
    }

    if ( !( 'language' in base ) ) {
        return {
            rule: rule,
            error: new Error( 'description rule must specifiy a language' )
        };
    }

    const languages = availableLanguages.map( l => l.code );
    if ( !languages.includes( base.language ) ) {
        return {
            rule: rule,
            error: new Error( `language "${base.language}" is not a supported language` )
        };
    }

    if ( !( 'scope' in base ) ) {
        return {
            rule: rule,
            error: new Error( 'missing required key scope' )
        };
    }

    if ( !allowedKeyScopes[ key ].includes( base.scope ) ) {
        return {
            rule: rule,
            error: new Error( `scope "${base.scope}" is not supported for key ${key}` )
        };
    }

    if ( base.scope === RuleScopeLogin && ( !( 'login' in base ) || base.login.length === 0 ) ) {
        return {
            rule: rule,
            error: new Error( `scope "${RuleScopeLogin}" requires a login be set for the rule` )
        };
    }

    if ( !( 'createdBy' in base ) ) {
        return {
            rule: rule,
            error: new Error( 'missing required key createdBy' )
        };
    }

    const template = ruleTemplates[ key ];

    rule = Object.assign( {}, template, base );

    if ( 'login' in rule && rule.login.length  === 0 ) {
        delete rule.login;
    }

    return {
        rule: rule,
        error: null
    };
};

const applyCapitalisation = ( sourceText ) => {
    const motorWaysHyphenated = '[A-Za-z]{1,3}-[0-9]{1,3}';
    const motorWaysNotHyphenated = '[A-Za-z]{1,3}[0-9]{1,3}';

    // https://en.wikipedia.org/wiki/List_of_airports_in_Spain
    const airports = [ 'MAD', 'BCN', 'PMI', 'AGP', 'ALC', 'LPA', 'TFS', 'IBZ', 'ACE', 'VLC' ];

    const exceptions = [
        'EPC', 'BBQ', 'LED', 'VOIP', 'WC',
        'GDPR', 'BREEAM', 'NIE', 'TIE', 'TV',
        'LCD', 'CD', 'DVD', 'WIFI', 'ADSL',
        'INE', 'IVA', 'VAT', 'IBI', 'PVC',
        'AJD', 'UNESCO',
        motorWaysHyphenated, motorWaysNotHyphenated
    ].concat( airports );

    const paragraphs = sourceText.split( '\n' );
    const escape = ( s ) => s.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' );

    const symbols = escape( '.,!¡?¿():;+-<>*' );

    const latinLetterUppercase = 'A-Z\u0041-\u0042-\u0043-\u0044-\u0045-\u0046-\u0047-\u0048-\u0049-\u004A-\u004B-\u004C-\u004D-\u004E-\u004F-\u0050-\u0051-\u0052-\u0053-\u0054-\u0055-\u0056-\u0057-\u0058-\u0059-\u005A-\u00C0-\u00C1-\u00C2-\u00C3-\u00C4-\u00C5-\u00C6-\u00C7-\u00C8-\u00C9-\u00CA-\u00CB-\u00CC-\u00CD-\u00CE-\u00CF-\u00D0-\u00D1-\u00D2-\u00D3-\u00D4-\u00D5-\u00D6-\u00D8-\u00D9-\u00DA-\u00DB-\u00DC-\u00DD-\u00DE-\u0100-\u0102-\u0104-\u0106-\u0108-\u010A-\u010C-\u010E-\u0110-\u0112-\u0114-\u0116-\u0118-\u011A-\u011C-\u011E-\u0120-\u0122-\u0124-\u0126-\u0128-\u012A-\u012C-\u012E-\u0130-\u0132-\u0134-\u0136-\u0139-\u013B-\u013D-\u013F-\u0141-\u0143-\u0145-\u0147-\u014A-\u014C-\u014E-\u0150-\u0152-\u0154-\u0156-\u0158-\u015A-\u015C-\u015E-\u0160-\u0162-\u0164-\u0166-\u0168-\u016A-\u016C-\u016E-\u0170-\u0172-\u0174-\u0176-\u0178-\u0179-\u017B-\u017D-\u0181-\u0182-\u0184-\u0186-\u0187-\u0189-\u018A-\u018B-\u018E-\u018F-\u0190-\u0191-\u0193-\u0194-\u0196-\u0197-\u0198-\u019C-\u019D-\u019F-\u01A0-\u01A2-\u01A4-\u01A6-\u01A7-\u01A9-\u01AC-\u01AE-\u01AF-\u01B1-\u01B2-\u01B3-\u01B5-\u01B7-\u01B8-\u01BC-\u01C4-\u01C7-\u01CA-\u01CD-\u01CF-\u01D1-\u01D3-\u01D5-\u01D7-\u01D9-\u01DB-\u01DE-\u01E0-\u01E2-\u01E4-\u01E6-\u01E8-\u01EA-\u01EC-\u01EE-\u01F1-\u01F4-\u01F6-\u01F7-\u01F8-\u01FA-\u01FC-\u01FE-\u0200-\u0202-\u0204-\u0206-\u0208-\u020A-\u020C-\u020E-\u0210-\u0212-\u0214-\u0216-\u0218-\u021A-\u021C-\u021E-\u0220-\u0222-\u0224-\u0226-\u0228-\u022A-\u022C-\u022E-\u0230-\u0232-\u023A-\u023B-\u023D-\u023E-\u0241-\u0243-\u0244-\u0245-\u0246-\u0248-\u024A-\u024C-\u024E-\u0370-\u0372-\u0376-\u037F-\u0386-\u0388-\u0389-\u038A-\u038C-\u038E-\u038F-\u0391-\u0392-\u0393-\u0394-\u0395-\u0396-\u0397-\u0398-\u0399-\u039A-\u039B-\u039C-\u039D-\u039E-\u039F-\u03A0-\u03A1-\u03A3';

    for ( let paragraph = 0; paragraph < paragraphs.length; paragraph++ ) {
        if ( paragraphs[ paragraph ].length === 0 ) {
            continue;
        }

        const words = paragraphs[ paragraph ].split( ' ' );
        const wordsO = [].concat( words );

        for ( let word = 0; word < words.length; word++ ) {
            const exceptionFound = exceptions.some( exceptionPattern => {
                const pattern = `^[${symbols}]{0,1}${exceptionPattern}[${symbols}]{0,1}$`;
                const regex = new RegExp( pattern, 'um' );
                return regex.test( words[ word ] );
            } );

            if ( exceptionFound || !words[ word ] ) {
                continue;
            }

            const startOfParagraph = word === 0;

            if ( startOfParagraph && words[ word ].length === 1 ) {
                continue;
            }

            const allUpper = new RegExp( `^[${latinLetterUppercase}]{2,}$`, 'um' );

            const previousWord = words[ word - 1 ];

            const startOfSentence = /[.?!]$/.test( previousWord );

            const isAllUpper = allUpper.test( words[ word ] );

            if ( startOfSentence && isAllUpper ) {
                words[ word ] = words[ word ][ 0 ].toUpperCase() + words[ word ].substr( 1 ).toLowerCase();
                continue;
            }

            if ( isAllUpper ) {
                words[ word ] = words[ word ].toLowerCase();

                if ( word === 0 ) {
                    words[ word ] = words[ word ][ 0 ].toUpperCase() + words[ word ].substr( 1 );
                }

                continue;
            }

            const startsOrEndsSpecial = new RegExp( `^[${symbols}]{0,}[${latinLetterUppercase}]{2,}[${symbols}]{0,}$`, 'um' );

            if ( startsOrEndsSpecial.test( words[ word ] ) && exceptions.every( x => words[ word ].indexOf( x ) === -1 ) ) {
                words[ word ] = words[ word ].toLowerCase();
                continue;
            }

            if ( word !== 0 && word !== words.length - 1 ) {
                const u = new RegExp( `^[${latinLetterUppercase}]*$`, 'um' );
                const surrounded = ( u.test( wordsO[ word - 1 ] ) && u.test( wordsO[ word + 1 ] ) );

                if ( u.test( words[ word ] ) && surrounded ) {
                    words[ word ] = words[ word ].toLowerCase();
                    continue;
                }
            }
        }

        paragraphs[ paragraph ] = words.join( ' ' );
    }

    return paragraphs.join( '\n' );
};

const applyRemoveRange = ( sourceText, rule ) => {
    const start = escape( rule.rangeStart );
    const end = escape( rule.rangeEnd );
    const pattern = new RegExp( `(${start}).*(${end})`, 'gis' );
    const newDescription = sourceText.replaceAll( pattern, '' );
    return newDescription;
};

const applyRemoveText = ( sourceText, rule ) => {
    const pattern = new RegExp( escape( rule.textToMatch ), 'gi' );
    const newDescription = sourceText.replaceAll( pattern, '' );
    return newDescription;
};

const applyRemoveIfContains = ( sourceText, rule ) => {
    if ( !sourceText.includes( rule.textToMatch ) ) {
        return sourceText;
    }

    const text = escape( rule.textToMatch );

    const patternForStart = new RegExp( `^.*(?:${text}).*\\n`, 'gi' );
    const patternForMiddle = new RegExp( `\\n.*(?:${text}).*\\n`, 'gi' );
    const patternForEnd = new RegExp( `\\n.*(?:${text}).*$`, 'gi' );

    let newDescription = sourceText;

    if ( patternForStart.test( sourceText ) ) {
        newDescription = newDescription.replaceAll( patternForStart, '' );
    }

    if ( patternForMiddle.test( sourceText ) ) {
        newDescription = newDescription.replaceAll( patternForMiddle, '' );
    }

    if ( patternForEnd.test( sourceText ) ) {
        newDescription = newDescription.replaceAll( patternForEnd, '' );
    }

    return newDescription;
};

const applyOverride = ( rule ) => {
    return rule.text;
};

const applyDescriptionRule = ( description, rule ) => {
    switch ( rule.key ) {
    case RuleKeyDescriptionCapitalisation:
        return applyCapitalisation( description );
    case RuleKeyDescriptionOverride:
        return applyOverride( rule );
    case RuleKeyDescriptionRemoveText:
        return applyRemoveText( description, rule );
    case RuleKeyDescriptionRemoveRange:
        return applyRemoveRange( description, rule );
    case RuleKeyDescriptionRemoveIfContains:
        return applyRemoveIfContains( description, rule );
    }
};

export {
    makeDescriptionRule,
    RuleKeyDescriptionRemoveText,
    RuleKeyDescriptionRemoveRange,
    RuleKeyDescriptionRemoveIfContains,
    RuleKeyDescriptionOverride,
    RuleKeyDescriptionCapitalisation,
    applyDescriptionRule
};



// WEBPACK FOOTER //
// ./src/components/property-qc/rules/description.js